import { useEffect, useMemo, useState } from "react";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fetchGallery, dealerIgStatus, saveSetupState } from "@/lib/api";
import { toast } from "sonner";
import { SETUP_STEPS, computeSetup } from "./setupRules";
import { StepProfile, StepBusiness, StepBrand, StepHome, StepConnect, StepMoney, StepLaunch } from "./SetupSteps";
import Fireworks from "./Fireworks";
import LaunchShareDialog from "./LaunchShareDialog";
import { StepHero } from "./StepHero";

export default function SetupTab({ recId, form, setForm, bind, save, saving, isDirty, slug, email, setupState, onSetupState, onComplete }) {
  const [galleryCount, setGalleryCount] = useState(0);
  const [igConnected, setIgConnected] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [flash, setFlash] = useState(0);
  const connectSkipped = !!setupState?.connect_skipped;
  const moneySkipped = !!setupState?.money_skipped;
  const launched = !!setupState?.launched;

  useEffect(() => {
    if (!recId) return;
    fetchGallery(recId).then((r) => setGalleryCount((r?.items || []).length)).catch(() => {});
    dealerIgStatus(recId).then((r) => setIgConnected(!!r?.connected)).catch(() => {});
  }, [recId]);

  const status = useMemo(() => computeSetup(form, { galleryCount, igConnected, connectSkipped, moneySkipped, launched }), [form, galleryCount, igConnected, connectSkipped, moneySkipped, launched]);
  const firstOpen = SETUP_STEPS.find((s) => !status.done[s.key])?.key || "launch";
  const [active, setActiveRaw] = useState(firstOpen);
  const [touched, setTouched] = useState(false);
  const setActive = (k) => { setTouched(true); setActiveRaw(k); setFlash((n) => n + 1); };
  useEffect(() => { if (!touched) setActiveRaw(firstOpen); }, [firstOpen, touched]);
  const idx = SETUP_STEPS.findIndex((s) => s.key === active);
  const step = SETUP_STEPS[idx];

  // Record step completion timestamps on the dealer record (feeds the onboarding report timeline).
  useEffect(() => {
    if (!setupState || !recId) return;
    const recorded = setupState.steps || {};
    const fresh = SETUP_STEPS.filter((s) => s.key !== "launch" && status.done[s.key] && !recorded[s.key]).map((s) => s.key);
    if (!fresh.length) return;
    saveSetupState(recId, { steps_done: fresh }).then(onSetupState).catch(() => {});
  }, [status.done, setupState, recId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveAndContinue() {
    if (isDirty) {
      const ok = await save();
      if (ok === false) return;
    }
    if (idx < SETUP_STEPS.length - 1) setActive(SETUP_STEPS[idx + 1].key);
  }
  async function skip(flag) {
    try {
      onSetupState(await saveSetupState(recId, { [flag]: true }));
      if (idx < SETUP_STEPS.length - 1) setActive(SETUP_STEPS[idx + 1].key);
    } catch {
      toast.error("Could not save — try again");
    }
  }
  async function launch() {
    if (isDirty) await save();
    try {
      onSetupState(await saveSetupState(recId, { launched: true, steps_done: ["launch"] }));
      setCelebrate(true);
    } catch {
      toast.error("Could not save — try again");
    }
  }

  const stepProps = { form, setForm, bind, recId };
  return (
    <div className="space-y-4" data-testid="setup-tab">
      <Fireworks active={celebrate} onDone={() => { setCelebrate(false); toast.success("Your dealer profile is live"); setShareOpen(true); }} />
      <LaunchShareDialog open={shareOpen} onOpenChange={(o) => { setShareOpen(o); if (!o) onComplete?.(); }} recId={recId} />

      <Card className="rounded-2xl border-zinc-200 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-zinc-900">Complete your OreVital dealer profile</h2>
            <p className="text-xs text-zinc-500">Seven simple steps so customers can discover, connect and buy from you.</p>
          </div>
          <div className="sm:w-72">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-violet-700" data-testid="setup-progress-text">{status.count} of {status.total} steps complete</span>
              <span className="text-zinc-500">{status.pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
              <div className="h-full bg-violet-600 transition-all" style={{ width: `${status.pct}%` }} data-testid="setup-progress-bar" />
            </div>
          </div>
        </div>

        {/* Step bar — fits the card, no scrollbar: numbered rail with a connecting line */}
        <div className="mt-5 grid gap-1" style={{ gridTemplateColumns: `repeat(${SETUP_STEPS.length}, minmax(0, 1fr))` }} data-testid="setup-steps">
          {SETUP_STEPS.map((s, i) => {
            const done = status.done[s.key];
            const on = s.key === active;
            return (
              <button key={s.key} type="button" onClick={() => setActive(s.key)} aria-pressed={on}
                className="group relative flex flex-col items-center text-center min-w-0 pt-1 pb-2 rounded-xl transition-colors hover:bg-zinc-50"
                data-testid={`setup-step-${s.key}`}>
                {i > 0 && <span className={`absolute left-0 right-1/2 top-[18px] h-0.5 ${status.done[SETUP_STEPS[i - 1].key] ? "bg-emerald-400" : "bg-zinc-200"}`} />}
                {i < SETUP_STEPS.length - 1 && <span className={`absolute left-1/2 right-0 top-[18px] h-0.5 ${done ? "bg-emerald-400" : "bg-zinc-200"}`} />}
                <span key={on ? flash : -1}
                  className={`relative z-10 inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ring-4 ring-white transition-transform ${on ? "step-flash" : ""} ${done ? "bg-emerald-500 text-white" : on ? "bg-violet-600 text-white" : "bg-zinc-200 text-zinc-600"}`}>
                  {done ? <Check size={14} strokeWidth={3} /> : s.n}
                </span>
                <span className={`mt-1.5 text-[10px] sm:text-[11px] font-semibold leading-tight truncate w-full px-0.5 ${on ? "text-violet-800" : "text-zinc-700"}`}>{s.title}</span>
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="rounded-2xl border-zinc-200 p-4 sm:p-5" data-testid={`setup-card-${step.key}`}>
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-violet-600 text-white text-xs font-bold">{step.n}</span>
          <div>
            <div className="font-semibold text-zinc-900">{step.title}</div>
            <div className="text-xs text-zinc-500">{step.blurb}</div>
          </div>
        </div>

        <StepHero stepKey={step.key} />

        {step.key === "profile" && <StepProfile {...stepProps} slug={slug} />}
        {step.key === "business" && <StepBusiness {...stepProps} />}
        {step.key === "brand" && <StepBrand {...stepProps} galleryCount={galleryCount} />}
        {step.key === "home" && <StepHome {...stepProps} slug={slug} />}
        {step.key === "connect" && <StepConnect bind={bind} recId={recId} onSkip={() => skip("connect_skipped")} skipped={connectSkipped} />}
        {step.key === "money" && <StepMoney bind={bind} recId={recId} email={email} onSkip={() => skip("money_skipped")} skipped={moneySkipped} />}
        {step.key === "launch" && <StepLaunch form={form} slug={slug} launched={launched} onLaunch={launch} onShare={() => setShareOpen(true)} />}

        {step.key !== "launch" && (
          <Button onClick={saveAndContinue} disabled={saving} className="mt-5 w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="setup-save-continue">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <>{isDirty ? "Save & Continue" : "Continue"} <ArrowRight size={16} className="ml-1.5" /></>}
          </Button>
        )}
      </Card>
    </div>
  );
}
